import React, { useMemo, useCallback } from 'react';
import Promise from 'bluebird';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { ImagePickerContainer } from '@bingads-webui-react/image-picker';
import { LoadingPlaceholder } from '@bingads-webui-campaign-react/image-experience';

import getUserVideoItemWrapper from './user-video-wrapper';
import { FormatVideoError } from '../../util/format-video-error';
import { ImageExperienceVideo } from '../../model/image-experience-video';

const itemHeight = 180;
const getPickerHeight = () => {
  if (window.innerHeight > 1000) {
    return 580;
  } else if (window.innerHeight > 870) {
    return 450;
  }
  return 350;
};

const loadingPlaceholder = () => (<LoadingPlaceholder />);

const UserVideoPickerContainer = (props) => {
  const {
    i18n,
    userVideoDataSource,
    onVideoSelected,
    videoRepository,
    showUiBlocker,
    showErrors,
    permissions,
    isLazyLoadEnabled,
    videoList,
    videoPickerContainerHeight,
  } = props;

  const onUserVideoDelete = useCallback((id) => {
    if (!confirm(i18n.getString('ImageExperience_Video_Delete_Confirm'))) { // eslint-disable-line
      return Promise.resolve(false);
    }
    if (videoList.find(video => video.IdString === id)) {
      showErrors([i18n.getString('ImageExperience_Media_Delete_Error_UseInDraft')]);
      return Promise.resolve(false);
    }

    showUiBlocker(true);
    showErrors([]);

    return videoRepository
      .deleteVideo({
        id,
      })
      .then(() => {
        userVideoDataSource.reload();
      })
      .catch((response) => {
        showErrors(FormatVideoError(response, i18n));
      }).finally(() => {
        showUiBlocker(false);
      });
  }, [i18n, videoRepository, userVideoDataSource, showUiBlocker, showErrors, videoList]);

  const onUserVideoEdit = useCallback((id, text) => {
    showUiBlocker(true);
    showErrors([]);

    return videoRepository.patchVideo({
      id,
      deltaVideo: { Description: text },
    }).then(() => userVideoDataSource.reload()).finally(() => {
      showUiBlocker(false);
    }).catch(() => showErrors([i18n.getString('ImageExperience_VideoEditFail')]));
  }, [videoRepository, userVideoDataSource, showUiBlocker, showErrors, i18n]);

  const onUserVideoSelected = (selectedItems) => {
    const item = selectedItems[0];
    onVideoSelected({ ...item, src: item.videoSrc });
  };

  const isGoogleImportVideoAdsEnabled = permissions.IsGoogleImportVideoAdsEnabled;

  const userVideoItemWrapper = useMemo(() => getUserVideoItemWrapper({
    i18n,
    onUserVideoDelete,
    videoRepository,
    isGoogleImportVideoAdsEnabled,
    userVideoDataSource,
    showUiBlocker,
    onUserVideoEdit,
  }), [
    i18n,
    isGoogleImportVideoAdsEnabled,
    onUserVideoDelete,
    onUserVideoEdit,
    videoRepository,
    showUiBlocker,
    userVideoDataSource,
  ]);

  const onLoadMore = useCallback(() => {
    userVideoDataSource.loadMore();
  }, [userVideoDataSource]);

  return (
    <div className="user-image-picker-container">
      <ImagePickerContainer
        i18n={i18n}
        dataSource={userVideoDataSource}
        isMultiSelection={false}
        onSelectionChanged={onUserVideoSelected}
        isLazyLoadEnabled={isLazyLoadEnabled}
        onLoadMore={onLoadMore}
        isAutoLoadMore
        height={videoPickerContainerHeight || getPickerHeight()}
        itemHeight={itemHeight}
        imageListItemWrapper={userVideoItemWrapper}
        imagePlaceHolder={loadingPlaceholder}
        useBingAdsLoader
      />
    </div>
  );
};

UserVideoPickerContainer.propTypes = {
  userVideoDataSource: PropTypes.shape({
    reload: PropTypes.func.isRequired,
    loadMore: PropTypes.func.isRequired,
    searchAsync: PropTypes.func.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  showErrors: PropTypes.func,
  videoRepository: PropTypes.shape({
    deleteVideo: PropTypes.func.isRequired,
    patchVideo: PropTypes.func.isRequired,
  }).isRequired,
  permissions: PropTypes.shape({
    IsGoogleImportVideoAdsEnabled: PropTypes.bool,
  }),
  onVideoSelected: PropTypes.func.isRequired,
  showUiBlocker: PropTypes.func,
  isLazyLoadEnabled: PropTypes.bool.isRequired,
  videoList: PropTypes.arrayOf(PropTypes.instanceOf(ImageExperienceVideo)),
  videoPickerContainerHeight: PropTypes.number,
};

UserVideoPickerContainer.defaultProps = {
  showErrors: _.noop,
  showUiBlocker: _.noop,
  videoList: [],
  permissions: {},
  videoPickerContainerHeight: null,
};

export default UserVideoPickerContainer;
